import { NavLink, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, LayoutGrid, SlidersHorizontal, Coins, History, type LucideIcon } from "lucide-react";
import type { SuperAdminOrganizationDetail } from "@multi-chat/shared";
import { Button } from "../../components/ui/button/index.js";
import { SimpleTooltip } from "../../components/ui/tooltip/index.js";
import { SheetDescription, SheetTitle } from "../../components/ui/sheet/index.js";
import { cn } from "../../lib/utils.js";
import { WorkspaceInternalSidebarHeader } from "../../components/layout/WorkspaceInternalSidebarHeader.js";
import {
  workspaceInternalNavItemClass,
  workspaceInternalNavLabelClass,
} from "../../components/layout/workspace-internal-sidebar.js";
import {
  contactSheetDescriptionClass,
  contactSheetHeaderClass,
  contactSheetTitleClass,
} from "../contacts/contact-sheet-presentation.js";

type SuperAdminOrgDetailNavItem = {
  readonly id: string;
  readonly pathSuffix: string;
  readonly labelKey: string;
  readonly icon: LucideIcon;
  readonly testId: string;
};

const NAV_ITEMS: readonly SuperAdminOrgDetailNavItem[] = [
  {
    id: "overview",
    pathSuffix: "",
    labelKey: "superAdmin.detail.tabs.overview",
    icon: LayoutGrid,
    testId: "super-admin-org-nav-overview",
  },
  {
    id: "limits",
    pathSuffix: "/limits",
    labelKey: "superAdmin.detail.tabs.limits",
    icon: SlidersHorizontal,
    testId: "super-admin-org-nav-limits",
  },
  {
    id: "whatsapp",
    pathSuffix: "/whatsapp",
    labelKey: "superAdmin.detail.tabs.whatsapp",
    icon: Coins,
    testId: "super-admin-org-nav-whatsapp",
  },
  {
    id: "activity",
    pathSuffix: "/activity",
    labelKey: "superAdmin.detail.tabs.activity",
    icon: History,
    testId: "super-admin-org-nav-activity",
  },
];

export function SuperAdminOrgDetailSidebar({
  organization,
  collapsed,
  onCollapsedChange,
  onNavigate,
  onClose,
  variant = "sidebar",
}: {
  organization: SuperAdminOrganizationDetail;
  collapsed: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  onNavigate?: () => void;
  onClose?: () => void;
  variant?: "sidebar" | "drawer";
}): React.JSX.Element {
  const { t } = useTranslation();
  const isDrawer = variant === "drawer";
  const showLabels = isDrawer || !collapsed;
  const params = useParams();
  const orgId = params.organizationId ?? organization.id.toString();
  const basePath = `/super-admin/organizations/${orgId}`;

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col bg-surface"
      data-testid="super-admin-org-detail-sidebar-container"
      data-collapsed={collapsed && !isDrawer ? "true" : "false"}
    >
      {isDrawer ? (
        <header className={contactSheetHeaderClass}>
          <div className="grid min-w-0 gap-1">
            <SheetTitle className={contactSheetTitleClass}>{organization.name}</SheetTitle>
            <SheetDescription className={contactSheetDescriptionClass}>
              {t("superAdmin.detail.id")} #{organization.id}
            </SheetDescription>
          </div>
          {onClose ? (
            <Button
              type="button"
              variant="ghost"
              className="size-8 shrink-0 p-0 text-muted hover:text-foreground"
              onClick={onClose}
              aria-label={t("common.close")}
            >
              <X className="size-4" aria-hidden="true" />
            </Button>
          ) : null}
        </header>
      ) : (
        <WorkspaceInternalSidebarHeader
          title={t("superAdmin.settings.nav.mobileMenu")}
          collapsed={collapsed}
          onCollapsedChange={onCollapsedChange}
          expandLabel={t("superAdmin.settings.nav.expandSidebar")}
          collapseLabel={t("superAdmin.settings.nav.collapseSidebar")}
          collapseTestId="super-admin-org-sidebar-collapse"
        />
      )}

      <nav
        className={cn("min-h-0 flex-1 overflow-y-auto px-1.5 py-2", isDrawer && "px-3 py-3")}
        aria-label={t("superAdmin.detail.tabs.ariaLabel")}
        data-testid="super-admin-org-detail-nav"
      >
        <ul className="m-0 grid list-none gap-0.5 p-0">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const label = t(item.labelKey);
            const link = (
              <NavLink
                to={`${basePath}${item.pathSuffix}`}
                end={item.pathSuffix === ""}
                onClick={onNavigate}
                className={({ isActive }) =>
                  workspaceInternalNavItemClass(isActive, collapsed && !isDrawer)
                }
                aria-label={label}
                data-testid={item.testId}
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      size={16}
                      className={cn("shrink-0", isActive ? "text-accent" : "text-muted")}
                      aria-hidden="true"
                    />
                    <span className={workspaceInternalNavLabelClass(showLabels)}>
                      {label}
                    </span>
                  </>
                )}
              </NavLink>
            );

            return (
              <li key={item.id}>
                {collapsed && !isDrawer ? (
                  <SimpleTooltip content={label}>
                    <div className="w-full">{link}</div>
                  </SimpleTooltip>
                ) : (
                  link
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}